/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class SortActionAPI
 * @hideconstructor
 * @classdesc
 */
define([
	'../../../../widgets/livewidget/nls/StringResources',
	'./CustomSortAction',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../api/SlotActionsProviderAPI',
	'../../../../apiHelpers/SlotAPIHelper'
], (stringResources, CustomSortAction, APIFactory, SlotActionsProviderAPI, SlotAPIHelper) => {

	let AUTO_SORT_ORDER = {
		'asc': stringResources.get('dlg_ascending'),
		'desc': stringResources.get('dlg_descending'),
		'none': stringResources.get('dlg_none'),
		'custom': stringResources.get('dlg_custom'),
		'hierarchy': stringResources.get('dlg_hierarchy'),
		'namedSet': stringResources.get('dlg_namedSet'),
		'rank': stringResources.get('dlg_rank')
	};

	return class SortAction {

		constructor({ content, dashboardAPI, features }) {
			this.content = content;
			this.dashboardApi = dashboardAPI;
			this.features = features;
			this._autoSortOrder = 'none';

			this.customSortAction = new CustomSortAction({ content, dashboardAPI, features });

			this.content.getFeature('SlotActions').registerProvider('sortAction', this.getAPI());
		}

		getViewState(dataSlotId, itemIndex) {
			const dataSlot = this.getVisualization().getSlots().getSlot(dataSlotId);
			if(dataSlot) {
				return {
					height: 140,
					width: 140, // those are hints only; for the placement of the toolbar.
					getCurrentSelectedItem: this._getCurrentSelectedItem.bind(this, dataSlot, itemIndex),
					possibleItems: this._getPossibleItems(dataSlot, itemIndex)
				};
			}
			return null;
		}

		_getCurrentSelectedItem(dataSlot, itemIndex){
			let dataItem = dataSlot.getDataItemList()[itemIndex];
			const currentSort = dataItem.getSort();

			let sort = 'auto';

			if (!this.isRank && this.isCurrentSortRankSort(currentSort)) {
				sort = 'auto';
			} else if (this.isRank && !this.isCurrentSortRankSort(currentSort)) {
				sort = 'auto';
			} else if(currentSort && !dataItem.hasDefaultSort()) {
				sort = (currentSort.custom && currentSort.custom.enabled ? 'custom' : currentSort.type);
			}

			return sort;
		}

		isCurrentSortRankSort(currentSort) {
			// We use context to decide whether the current sort is rank sort because context is only used by rank
			return currentSort && currentSort.context;
		}

		_getPossibleItems(dataSlot, itemIndex) {
			this._autoSortOrderStringId = this.getDefaultSortOrder(dataSlot, itemIndex);
			const items = [{
				id: 'asc',
				localizedString: stringResources.get('toolbarActionSortAscending')
			}, {
				id: 'desc',
				localizedString: stringResources.get('toolbarActionSortDescending')
			}, {
				id: 'auto',
				localizedString: stringResources.get('toolbarActionSortAuto', {
					'sortOrder': AUTO_SORT_ORDER[this._autoSortOrderStringId]
				})
			}];

			if (this.customSortAction.isEnabled(dataSlot, itemIndex)){
				items.push(this.customSortAction.getMenuItem());
			}

			return items;
		}

		getDefaultSortOrder (dataSlot, itemIndex) {
			let dataItem = dataSlot.getDataItemList()[itemIndex];
			if (dataItem) {
				let metadataColumn = dataItem.getMetadataColumn();
				let sort = metadataColumn && metadataColumn.getDefaultSortType();
				if (sort) {
					return sort;
				} else {
					if (metadataColumn && (metadataColumn.isOlapColumn() === true || metadataColumn.isNamedSet() === true)) {
						return this._getSortLabelForOlapItem(metadataColumn);
					}
					if(dataItem.getTopBottom()){
						return 'rank';
					}
					let slotDef = dataSlot.getDefinition();
					if (slotDef) {
						// Rank slot is built from an existing slot that may include a default sort, skip it for rank slot.
						return slotDef.getSort() || 'none';
					}
				}
			}
			return 'none';
		}

		_getSortLabelForOlapItem(metadataColumn) {
			if (metadataColumn.isNamedSet()) {
				return 'namedSet';
			}
			if (metadataColumn.getType() !== 'fact') {
				return 'hierarchy';
			}
			return 'none';
		}

		_supportAction(dataSlot, mapIndex) {
			if(Array.isArray(mapIndex) && mapIndex.length !== 1) {
				return false;
			}

			return !this._isMultiMeasureDataSlot(dataSlot, mapIndex) &&
			this.getVisualization().getDefinition().getProperty('supportsSortAction') &&
			SlotAPIHelper.isSlotSortable(dataSlot, mapIndex, this.getVisualization()) &&
			!((mapIndex !== undefined && mapIndex !== null) && !dataSlot.getDataItemList()[mapIndex].getMetadataColumn());
		}

		_isMultiMeasureDataSlot (dataSlot, itemIndex) {
			if(itemIndex === undefined && dataSlot){
				return SlotAPIHelper.isMultiMeasuresSeriesSlot(dataSlot) ||SlotAPIHelper.isMultiMeasuresValueSlot(dataSlot);
			}

			return SlotAPIHelper.isMultiMeasuresSeriesOrValue(dataSlot, itemIndex);
		}

		getSlotActionList(dataSlotId, itemIndex, options) {
			this.isRank = options && options.rankSort;
			const dataSlot = this.getVisualization().getSlots().getSlot(dataSlotId);
			if(dataSlot && this._supportAction(dataSlot, itemIndex)) {
				itemIndex = Array.isArray(itemIndex) && itemIndex.length === 1 ? itemIndex[0] : parseInt(itemIndex);
				this.itemIndex = isNaN(itemIndex) ? 0 : itemIndex;
				this.dataSlotId = dataSlotId;
				let viewOptions = this.getViewState(dataSlotId, this.itemIndex);
				const sortIcon = this.features['Dashboard.Icons'].getIcon('common-sort-icon');

				return [{
					name: 'sortAction',
					label: stringResources.get('toolbarActionSort'),
					icon: sortIcon.id,
					type: 'SubMenu',
					view: {
						module: 'dashboard-analytics/lib/@waca/dashboard-common/dist/ui/dialogs/CommonSelectItemsDialog',
						state: viewOptions
					},
					actions: {
						apply: this.handleSelection.bind(this, dataSlot, options)
					}
				}];
			}
			return [];
		}

		handleSelection(dataSlot, options, selectedItem) {
			const dataItem = dataSlot.getDataItemList()[this.itemIndex];

			if (selectedItem === this.customSortAction.getMenuItem().id) {
				this.customSortAction.handleSelection(dataItem, AUTO_SORT_ORDER[this._autoSortOrderStringId], this._autoSortOrderStringId);
			} else {
				let params = {
					type: selectedItem === 'auto' ? null : selectedItem
				};

				// Set rankSort parameter if passed in via options
				// Also make sure to include the topbottom context.
				if (options && options.rankSort) {
					const topBottom = dataItem.getTopBottom();
					if (topBottom && topBottom.context && topBottom.context.itemId) {
						params.context = topBottom.context.itemId;
					}
					params.rankSort = true;
				}

				dataItem.setSort(params);
			}
		}

		getVisualization() {
			if (!this.visualization) {
				this.visualization = this.content.getFeature('Visualization');
			}
			return this.visualization;
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [SlotActionsProviderAPI]);
			}
			return this._api;
		}
	};
});